import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Combobox, ComboboxItem, EmptyState } from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { CSATLaunch } from 'components/CSAT/CSATLaunch'
import { CriteriaKeys } from 'components/CSAT/types'
import { PageHeader } from 'components/header/PageHeader'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { PaymentCaseType } from 'types/payments'
import { convertLabelToKey } from 'utils/utils'
import { PaymentSupportTable } from './components/PaymentSupportTable'
import { paymentCaseTypeFilterOptions } from './constants'
import useCaseListApi from './hooks/useCaseListApi'
import useCSATSurvey from './hooks/useCSATSurvey'

export const PaymentSupport = () => {
  const {
    CSATPreferenceData,
    enableCsatSurveyCasePortal,
    getIsCriteriaCompleted,
    isFetchingAccountPreferences,
    completeCSATCriteria,
  } = useCSATSurvey({
    shouldInitialiseCSATSurvey: true,
  })

  const [showCSATSurvey, setShowCSATSurvey] = useState(false)

  const isFilteredCaseStatusCriteriaCompleted = getIsCriteriaCompleted(
    CriteriaKeys.HAS_FILTERED_CASE_STATUS
  )

  const permissions = useMappedRoles()
  const canViewPaymentSupport = permissions?.paymentSupport?.canView
  const { t } = useTranslation()
  const [selectedFilterOptions, setSelectedFilterOptions] = useState<PaymentCaseType[]>([])

  useEffect(() => {
    const hasCompletedCriteria =
      !!CSATPreferenceData?.criteria &&
      Object.values(CSATPreferenceData?.criteria)?.indexOf(false) === -1

    const showCSATSurvey =
      enableCsatSurveyCasePortal && hasCompletedCriteria && !CSATPreferenceData?.isSurveyCompleted

    setShowCSATSurvey(showCSATSurvey)
  }, [isFetchingAccountPreferences, enableCsatSurveyCasePortal, CSATPreferenceData?.criteria])

  const checkboxSelectOptions = paymentCaseTypeFilterOptions?.map<ComboboxItem>(paymentCaseType => {
    let label = convertLabelToKey(paymentCaseType)

    if (paymentCaseType == PaymentCaseType.UNRESOLVED) {
      label = convertLabelToKey(PaymentCaseType.PENDING)
    }

    const translatedLabel = t(`paymentSupport.filterStatus.${label}`)
    return { value: paymentCaseType, leftText: translatedLabel }
  })

  const handleClearFilters = () => {
    setSelectedFilterOptions([])
    !isFilteredCaseStatusCriteriaCompleted && enableCsatSurveyCasePortal && handleCSATSurveyUpdate()
  }

  const handleCSATSurveyUpdate = () => {
    completeCSATCriteria(CriteriaKeys.HAS_FILTERED_CASE_STATUS)
  }

  const mappedStatuses = useMemo(() => {
    if (selectedFilterOptions?.includes(PaymentCaseType.IN_PROGRESS)) {
      return [...selectedFilterOptions, PaymentCaseType.ESCALATED, PaymentCaseType.ON_HOLD]
    }
    return selectedFilterOptions
  }, [selectedFilterOptions])

  const {
    isFetching,
    isError,
    data: { caseListPagination, casesList },
    actions: { setCaseListPagination, onPageChange },
  } = useCaseListApi(mappedStatuses)

  const { paging, pagingMax } = caseListPagination

  const shouldShowFilter = useMemo(() => {
    if (isError) {
      return false
    }

    if (casesList?.length === 0 && selectedFilterOptions.length === 0) {
      return false
    }

    return true
  }, [isError, casesList, selectedFilterOptions])

  return (
    <>
      <PageHeader description={t('paymentSupport.subTitle')} />
      {canViewPaymentSupport ? (
        <>
          {shouldShowFilter && (
            <Box flex gapX={12} paddingB={24}>
              <Box className="w-[180px]">
                <Combobox
                  items={checkboxSelectOptions}
                  id="case-portal-checkbox-select"
                  testId="case-portal-checkbox-select"
                  searchPlaceholder={t('paymentSupport.status')}
                  checkedValues={selectedFilterOptions}
                  onCheckedChange={(value: PaymentCaseType) => {
                    setSelectedFilterOptions(current => {
                      if (current?.includes(value)) {
                        return current?.filter(item => item !== value)
                      }
                      return [...current, value]
                    })
                  }}
                />
              </Box>
              {!!selectedFilterOptions?.length && (
                <Button noPadding variant="ghost" onClick={handleClearFilters}>
                  {t('clearFilters')}
                </Button>
              )}
            </Box>
          )}
          <PaymentSupportTable
            isFetching={isFetching}
            isError={isError}
            casesList={casesList}
            paging={paging}
            pagingMax={pagingMax}
            setCaseListPagination={setCaseListPagination}
            onPageChange={onPageChange}
          />
          {showCSATSurvey && <CSATLaunch />}
        </>
      ) : (
        <EmptyState
          testId="not-allowed"
          LottiePlayer={<Lottie src="permissions-denied" />}
          title={t('paymentSupport.permissionsErrorHeadline')}
          description={t('paymentSupport.permissionsErrorDescription')}
        />
      )}
    </>
  )
}
